import React from 'react';
import { useTranslation } from 'react-i18next';
import noPreviewImage from '../../../assets/images/No Preview Yet.png';

interface CanvaDesignThumbnail {
  url: string;
}

interface CanvaDesign {
  id: string;
  title?: string;
  page_count?: number;
  updated_at?: number;
  thumbnail?: CanvaDesignThumbnail;
}

interface CanvaDesignsModalProps {
  isOpen: boolean;
  isExporting: boolean;
  isRefreshing: boolean;
  canvaDesigns: any[];
  canvaDesignsPage: number;
  selectedCanvaDesigns: any[];
  onClose: () => void;
  onToggleSelect: (design: any) => void;
  onImportSelected: () => void;
  onRefresh: () => void;
  setPage: (page: number) => void;
  canvaLogo: string;
}

export const CanvaDesignsModal: React.FC<CanvaDesignsModalProps> = ({
  isOpen,
  isExporting,
  isRefreshing,
  canvaDesigns,
  canvaDesignsPage,
  selectedCanvaDesigns,
  onClose,
  onToggleSelect,
  onImportSelected,
  onRefresh,
  setPage,
  canvaLogo,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const totalPages = Math.ceil((canvaDesigns?.length || 0) / 10);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 lg:ml-16">
        <div
          className="fixed inset-0 bg-black bg-opacity-25"
          onClick={() => {
            if (!isExporting) onClose();
          }}
        />

        <div className="relative bg-white rounded-xl border border-black shadow-xl w-[85vw] lg:w-[calc(90vw-4rem)] max-w-none max-h-[90vh] overflow-hidden">
          {isExporting && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-xl">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-700 font-medium">{t('canva.exportingDesigns', 'Exporting designs...')}</p>
                <p className="text-gray-500 text-sm mt-1">{t('canva.preparingImages', 'Please wait while we prepare your images')}</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img src={canvaLogo} alt="Canva" className="w-6 h-6" />
              <h2 className="text-xl font-semibold">
                {selectedCanvaDesigns.length > 0
                  ? selectedCanvaDesigns.length === 1
                    ? t('canva.selectDesignSingular', 'Select Canva Designs ({{count}} selected)', { count: selectedCanvaDesigns.length })
                    : t('canva.selectDesignsPlural', 'Select Canva Designs ({{count}} selected)', { count: selectedCanvaDesigns.length })
                  : t('canva.selectDesign', 'Select a Canva Design')}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {selectedCanvaDesigns.length > 0 && (
                <button
                  onClick={onImportSelected}
                  disabled={isExporting}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                      {t('canva.exporting', 'Exporting...', { count: selectedCanvaDesigns.length })}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {selectedCanvaDesigns.length === 1
                        ? t('canva.addMedia', 'Add 1 media', { count: selectedCanvaDesigns.length })
                        : t('canva.addMedias', 'Add {{count}} medias', { count: selectedCanvaDesigns.length })}
                    </>
                  )}
                </button>
              )}
              <button
                onClick={onRefresh}
                disabled={isExporting || isRefreshing}
                className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                title={t('canva.refreshDesigns', 'Refresh designs')}
              >
                {isRefreshing ? (
                  <div className="w-4 h-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
              <button
                onClick={onClose}
                disabled={isExporting}
                className="text-gray-400 hover:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title={isExporting ? t('canva.waitForExport', 'Please wait for export to complete') : t('common.close', 'Close')}
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {canvaDesigns.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {canvaDesigns.slice(canvaDesignsPage * 10, (canvaDesignsPage + 1) * 10).map((design: CanvaDesign) => {
                    const isSelected = selectedCanvaDesigns.some((selected: any) => selected.id === design.id);
                    return (
                      <div
                        key={design.id}
                        onClick={() => onToggleSelect(design)}
                        className={`group cursor-pointer border-2 rounded-lg overflow-hidden hover:shadow-md transition-all duration-200 ${
                          isSelected ? 'border-primary-500 bg-primary-50 shadow-md' : 'border-gray-200 hover:border-primary-500'
                        }`}
                      >
                        <div className="aspect-square bg-gray-100 relative">
                          {design.thumbnail ? (
                            <img
                              src={design.thumbnail.url}
                              alt={design.title || 'Canva Design'}
                              title={design.thumbnail.url}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img
                              src={noPreviewImage}
                              alt={t('canva.noPreview', 'No preview')}
                              className="w-full h-full object-contain bg-gray-100"
                            />
                          )}
                          <div className="absolute top-2 left-2">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-primary-500 border-primary-500' : 'bg-white border-gray-300 group-hover:border-primary-500'
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                            <div className="bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">{design.title || 'Untitled Design'}</h3>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>
                              {typeof design.page_count === 'number' ? (
                                <>
                                  {design.page_count} page{design.page_count !== 1 ? 's' : ''}
                                </>
                              ) : null}
                            </span>
                            <span>{design.updated_at ? new Date(design.updated_at * 1000).toLocaleDateString() : ''}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-6">
                  <button
                    onClick={() => setPage(Math.max(0, canvaDesignsPage - 1))}
                    disabled={canvaDesignsPage === 0}
                    className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t('common.previous', 'Previous')}
                  </button>
                  <div className="text-sm text-gray-500">
                    {t('canva.page', 'Page {{current}} of {{total}}', { current: canvaDesignsPage + 1, total: totalPages })}
                  </div>
                  <button
                    onClick={() => setPage(Math.min(Math.floor((canvaDesigns.length || 0) / 10), canvaDesignsPage + 1))}
                    disabled={canvaDesignsPage >= Math.floor((canvaDesigns.length || 0) / 10)}
                    className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('common.next', 'Next')}
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <img src={canvaLogo} alt="Canva" className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('canva.noDesigns', 'No designs found')}</h3>
                <p className="text-gray-500 mb-4">{t('canva.noDesignsDesc', "You don't have any designs in your Canva account yet.")}</p>
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <img src={canvaLogo} alt="Canva" className="w-4 h-4 mr-2" />
                  {t('canva.createNew', 'Create new design')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


